import {
  FOR_PURPOSE_LEVERS,
  blankGoal,
  fieldPath,
  goalLabel,
  newId,
  type Goal,
  type LeverDef,
  type LockableField,
  type Operation,
  type ValueMap,
} from "@valuemap/core";
import { useRef, useState } from "react";
import { useFieldLocks } from "./live/FieldLocks.js";
import styles from "./Edit.module.css";
import extra from "./EditExtra.module.css";
import ui from "./Ui.module.css";
import { Readback } from "./Readback.js";
import { ConfirmDialog } from "./ui/Modal.js";
import { useAutoGrow } from "./ui/useAutoGrow.js";

/**
 * The Map view (reference "Edit" mode): the setup strip, the goals rail and the value chain
 * editor. Wording and layout follow the reference. Every change is an operation (see
 * `@valuemap/core`), so the same edit works for one person now and for a workshop later.
 */

export type Dispatch = (op: Operation, options?: { coalesce?: boolean }) => void;

interface Props {
  map: ValueMap;
  canEdit: boolean;
  /** Whether goals can be removed (staff owners and editors). Defaults to `canEdit`. */
  canDelete?: boolean;
  dispatch: Dispatch;
  activeId: string | null;
  onSelect: (goalId: string | null) => void;
}

function Check() {
  return (
    <span className={styles.check} aria-hidden="true">
      <svg viewBox="0 0 12 12">
        <path
          d="M1 6l3.5 3.5L11 2"
          fill="none"
          stroke="#fff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function Setup({ map, canEdit, dispatch }: Pick<Props, "map" | "canEdit" | "dispatch">) {
  const set = (path: string, value: string) =>
    dispatch({ type: "setField", path, value }, { coalesce: true });
  return (
    <section className={`${styles.setup} no-print`} aria-label="Workshop details">
      <div className={`${styles.field} ${styles.org}`}>
        <label htmlFor="org-name">Organisation</label>
        <input
          id="org-name"
          type="text"
          placeholder="Client organisation name"
          autoComplete="off"
          readOnly={!canEdit}
          value={map.org.name}
          onChange={(e) => set("org.name", e.target.value)}
        />
      </div>
      <div className={`${styles.field} ${styles.small}`}>
        <label htmlFor="org-fac">Facilitated by</label>
        <input
          id="org-fac"
          type="text"
          placeholder="Your name"
          autoComplete="off"
          readOnly={!canEdit}
          value={map.org.facilitator}
          onChange={(e) => set("org.facilitator", e.target.value)}
        />
      </div>
      <div className={`${styles.field} ${styles.small}`}>
        <label htmlFor="org-date">Date</label>
        <input
          id="org-date"
          type="date"
          readOnly={!canEdit}
          value={map.org.date}
          onChange={(e) => dispatch({ type: "setField", path: "org.date", value: e.target.value })}
        />
      </div>
      <p className={extra.dataNote}>
        Use business information only. Do not enter details about individual clients, patients,
        donors or members.
      </p>
    </section>
  );
}

/**
 * A long text field with a soft lock (brief 8.3): focusing it claims it, and while someone else is
 * typing in it, it is read-only here and says who has it.
 */
function LockedTextarea({
  id,
  goalId,
  field,
  value,
  placeholder,
  readOnly,
  onChange,
}: {
  id: string;
  goalId: string;
  field: LockableField;
  value: string;
  placeholder: string;
  readOnly: boolean;
  onChange: (v: string) => void;
}) {
  const locks = useFieldLocks();
  const path = fieldPath(goalId, field);
  const holder = locks.lockedBy(path);
  const ref = useRef<HTMLTextAreaElement>(null);
  useAutoGrow(ref, value);
  return (
    <>
      <textarea
        ref={ref}
        id={id}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly || holder !== null}
        aria-describedby={holder ? `${id}-lock` : undefined}
        className={holder ? extra.locked : undefined}
        onFocus={() => {
          if (!readOnly) locks.focus(path);
        }}
        onBlur={() => locks.blur(path)}
        onChange={(e) => onChange(e.target.value)}
      />
      {holder && (
        <div id={`${id}-lock`} className={extra.lockNote} role="status">
          {holder} is typing…
        </div>
      )}
    </>
  );
}

function TextStep({
  num,
  heading,
  question,
  placeholder,
  goalId,
  field,
  value,
  readOnly,
  onChange,
}: {
  num: number;
  question: string;
  heading: string;
  placeholder: string;
  goalId: string;
  field: LockableField;
  value: string;
  readOnly: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div className={styles.step}>
      <div className={styles.marker} aria-hidden="true">
        {num}
      </div>
      <div className={styles.q}>{question}</div>
      <h3>
        <label htmlFor={`step-${num}`}>{heading}</label>
      </h3>
      <LockedTextarea
        id={`step-${num}`}
        goalId={goalId}
        field={field}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={onChange}
      />
    </div>
  );
}

function LeverTile({
  def,
  goal,
  canEdit,
  dispatch,
}: {
  def: LeverDef;
  goal: Goal;
  canEdit: boolean;
  dispatch: Dispatch;
}) {
  const lever = goal.levers[def.key];
  return (
    <div className={`${styles.lever} ${lever.on ? styles.on : ""}`}>
      <button
        type="button"
        className={styles.toggle}
        aria-pressed={lever.on}
        disabled={!canEdit}
        onClick={() =>
          dispatch({
            type: "setField",
            goalId: goal.id,
            path: `levers.${def.key}.on`,
            value: !lever.on,
          })
        }
      >
        <Check />
        <span>
          <span className={styles.lvTitle}>{def.title}</span>
          <span className={styles.lvHelp}>{def.help}</span>
        </span>
      </button>
      {lever.on && (
        <div className={styles.measure}>
          <input
            type="text"
            aria-label={`Measure for ${def.title}`}
            value={lever.measure}
            placeholder={def.measureHint}
            readOnly={!canEdit}
            onChange={(e) =>
              dispatch(
                {
                  type: "setField",
                  goalId: goal.id,
                  path: `levers.${def.key}.measure`,
                  value: e.target.value,
                },
                { coalesce: true },
              )
            }
          />
        </div>
      )}
    </div>
  );
}

function CustomLeverTile({
  goal,
  lever,
  canEdit,
  canDelete,
  dispatch,
}: {
  goal: Goal;
  lever: Goal["custom"][number];
  canEdit: boolean;
  canDelete: boolean;
  dispatch: Dispatch;
}) {
  const set = (field: "name" | "measure", value: string) =>
    dispatch(
      { type: "setCustomLever", goalId: goal.id, leverId: lever.id, field, value },
      { coalesce: true },
    );
  return (
    <div className={`${styles.lever} ${styles.on}`}>
      <div className={styles.toggle}>
        <Check />
        <input
          className={styles.lvNameInput}
          type="text"
          aria-label="Value area name"
          placeholder="Name this value area"
          value={lever.name}
          readOnly={!canEdit}
          onChange={(e) => set("name", e.target.value)}
        />
        {canDelete && (
          <button
            type="button"
            className={styles.rmLever}
            onClick={() =>
              dispatch({ type: "removeCustomLever", goalId: goal.id, leverId: lever.id })
            }
          >
            Remove
          </button>
        )}
      </div>
      <div className={styles.measure}>
        <input
          type="text"
          aria-label="Unit they will measure"
          placeholder="The unit they will measure"
          value={lever.measure}
          readOnly={!canEdit}
          onChange={(e) => set("measure", e.target.value)}
        />
      </div>
    </div>
  );
}

function GoalEditor({
  map,
  goal,
  canEdit,
  canDelete,
  dispatch,
  onRemove,
}: {
  map: ValueMap;
  goal: Goal;
  canEdit: boolean;
  canDelete: boolean;
  dispatch: Dispatch;
  onRemove: () => void;
}) {
  const text = (path: string) => (value: string) =>
    dispatch({ type: "setField", goalId: goal.id, path, value }, { coalesce: true });
  return (
    <>
      <div className={styles.goalHead}>
        <input
          className={styles.goalTitleInput}
          type="text"
          aria-label="Goal name"
          placeholder="Name this goal, e.g. Grow regular giving"
          value={goal.title}
          readOnly={!canEdit}
          onChange={(e) => text("title")(e.target.value)}
        />
        {canDelete && (
          <button type="button" className={styles.delGoal} onClick={onRemove}>
            Remove
          </button>
        )}
      </div>

      <div className={styles.whoRow}>
        <div className={styles.whoBox}>
          <label htmlFor="wb-stake">Whose goal is this?</label>
          <input
            id="wb-stake"
            type="text"
            placeholder="Economic buyer or champion, and role"
            value={goal.stakeholder}
            readOnly={!canEdit}
            onChange={(e) => text("stakeholder")(e.target.value)}
          />
        </div>
        <div className={styles.whoBox}>
          <label htmlFor="wb-win">Personal win</label>
          <input
            id="wb-win"
            type="text"
            placeholder="What does success here mean for them personally?"
            value={goal.personalWin}
            readOnly={!canEdit}
            onChange={(e) => text("personalWin")(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.chain}>
        <TextStep
          num={1}
          question="Right now"
          heading="What is the situation today?"
          placeholder="The current reality and where it hurts, before anything changes."
          goalId={goal.id}
          field="rightNow"
          value={goal.rightNow}
          readOnly={!canEdit}
          onChange={text("rightNow")}
        />
        <TextStep
          num={2}
          question="Objective"
          heading="What are we trying to achieve?"
          placeholder="The outcome they want, in their words."
          goalId={goal.id}
          field="objective"
          value={goal.objective}
          readOnly={!canEdit}
          onChange={text("objective")}
        />
        <TextStep
          num={3}
          question="Capability"
          heading="What do we need to be able to do?"
          placeholder="The things they need to be able to do that they cannot today."
          goalId={goal.id}
          field="capability"
          value={goal.capability}
          readOnly={!canEdit}
          onChange={text("capability")}
        />
        <TextStep
          num={4}
          question="Outcome"
          heading="What changes for your clients, supporters or team?"
          placeholder="What is different day to day once this is working."
          goalId={goal.id}
          field="outcome"
          value={goal.outcome}
          readOnly={!canEdit}
          onChange={text("outcome")}
        />
        <div className={`${styles.step} ${styles.impact}`}>
          <div className={styles.marker} aria-hidden="true">
            5
          </div>
          <div className={styles.q}>Impact</div>
          <h3>Where does the value come from?</h3>
          <div className={styles.levers}>
            {FOR_PURPOSE_LEVERS.map((def) => (
              <LeverTile
                key={def.key}
                def={def}
                goal={goal}
                canEdit={canEdit}
                dispatch={dispatch}
              />
            ))}
            {goal.custom.map((c) => (
              <CustomLeverTile
                key={c.id}
                goal={goal}
                lever={c}
                canEdit={canEdit}
                canDelete={canDelete}
                dispatch={dispatch}
              />
            ))}
            {canEdit && (
              <button
                type="button"
                className={styles.addLever}
                onClick={() =>
                  dispatch({
                    type: "addCustomLever",
                    goalId: goal.id,
                    lever: { id: newId("c"), name: "", measure: "" },
                  })
                }
              >
                + Add another value area
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.coi}>
        <div className={styles.q}>Cost of inaction</div>
        <h3>
          <label htmlFor="coi">What happens, and what does it cost, if we do not act?</label>
        </h3>
        <LockedTextarea
          id="coi"
          goalId={goal.id}
          field="costInaction"
          value={goal.costInaction}
          placeholder="The consequence of the goal not being met, and the price of that."
          readOnly={!canEdit}
          onChange={text("costInaction")}
        />
      </div>

      <Readback goal={goal} orgName={map.org.name} />
    </>
  );
}

export function EditView({
  map,
  canEdit,
  canDelete = canEdit,
  dispatch,
  activeId,
  onSelect,
}: Props) {
  const [removing, setRemoving] = useState<Goal | null>(null);
  const active = map.goals.find((g) => g.id === activeId) ?? map.goals[0] ?? null;

  const addGoal = () => {
    const goal = blankGoal();
    dispatch({ type: "addGoal", goal });
    onSelect(goal.id);
    // Move to the new goal's name, as the reference does.
    setTimeout(
      () => document.querySelector<HTMLInputElement>(`.${styles.goalTitleInput}`)?.focus(),
      30,
    );
  };

  return (
    <>
      <Setup map={map} canEdit={canEdit} dispatch={dispatch} />
      <main className={`${styles.layout} no-print`}>
        <aside className={styles.rail} aria-label="The goals">
          <h2>The goals</h2>
          <div className={styles.goalList}>
            {map.goals.map((g, i) => (
              <button
                key={g.id}
                type="button"
                className={styles.goalChip}
                aria-current={g.id === active?.id}
                onClick={() => onSelect(g.id)}
              >
                <span className={styles.num}>{i + 1}</span>
                <span className={styles.txt}>{goalLabel(g, i)}</span>
                <span className={styles.dots} aria-hidden="true">
                  {FOR_PURPOSE_LEVERS.map((L) => (
                    <span
                      key={L.key}
                      className={`${styles.dot} ${g.levers[L.key].on ? styles.on : ""}`}
                    />
                  ))}
                </span>
              </button>
            ))}
          </div>
          {canEdit && (
            <button type="button" className={styles.addGoal} onClick={addGoal}>
              + Add a strategic goal
            </button>
          )}

          <div className={styles.railNote}>
            <strong>How to run this.</strong> Work one goal at a time, in the client&apos;s own
            words. Move down the chain, then name where the value lands and the unit they will
            measure. Finish on the cost of not acting.
          </div>
          <div className={styles.legend}>
            <div className={styles.li}>
              <span className={styles.sw} style={{ background: "var(--green-d)" }} /> Value area in
              play on this goal
            </div>
            <div className={styles.li}>
              <span className={styles.sw} style={{ background: "var(--border-strong)" }} /> Not
              selected
            </div>
          </div>
        </aside>

        <section className={styles.canvas} id="canvas">
          {!active ? (
            <div className={styles.empty}>
              <h2>Map the value, goal by goal</h2>
              <p>
                Add the first strategic goal this organisation wants to reach. For each one you will
                capture what they are trying to achieve, what that will change, and where the value
                shows up, all in their words.
              </p>
              {canEdit && (
                <button type="button" className={`${ui.btn} ${ui.primary}`} onClick={addGoal}>
                  + Add the first goal
                </button>
              )}
            </div>
          ) : (
            <GoalEditor
              key={active.id}
              map={map}
              goal={active}
              canEdit={canEdit}
              canDelete={canDelete}
              dispatch={dispatch}
              onRemove={() => setRemoving(active)}
            />
          )}
        </section>
      </main>

      {removing && (
        <ConfirmDialog
          title="Remove this goal?"
          message={`“${goalLabel(removing, map.goals.indexOf(removing))}” and everything captured against it will be deleted.`}
          confirmLabel="Remove"
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            const next = map.goals.find((g) => g.id !== removing.id);
            dispatch({ type: "removeGoal", goalId: removing.id });
            onSelect(next?.id ?? null);
            setRemoving(null);
          }}
        />
      )}
    </>
  );
}
